import axios from "axios";
import { API_URL, getAuthHeader } from ".";
import { FormDto } from "../Models/FormDto";
import { ResponseModel } from "../Models/ResponseModel";
import { FormResponseDto, UserData } from "../Models/FormResponseDto";
import { ShowcaseBoothMap } from "../Models/ShowcaseBoothMap";

interface FormResponsePayload {
  formId: number;
  submittedById: number;
  actingUserId?: number;
  answers: {
    fieldId: number;
    value: string;
    price?: number;
    users?: UserData[];
    optionUsers?: { [optionValue: string]: UserData[] };
  }[];
  userInfo?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    companyName?: string;
  };
}

class FormApi {
  static getForm(formId: number) {
    return axios.get<FormDto>(
      `${API_URL}/api/forms/${formId}`,
      getAuthHeader()
    );
  }

  static getShowcaseBoothMap(eventId?: number) {
    const query = eventId != null ? `?eventId=${eventId}` : "";
    return axios.get<ShowcaseBoothMap>(
      `${API_URL}/api/forms/showcase-booth-map${query}`,
      getAuthHeader()
    );
  }

  static getBoothAvailability(formId: number, fieldId: number) {
    return axios.get<
      {
        boothNumber: string;
        companyName: string;
        submittedDate: string;
        responseId: number;
      }[]
    >(
      `${API_URL}/api/forms/${formId}/fields/${fieldId}/booth-availability`,
      getAuthHeader()
    );
  }
  static getResponse(responseId: number) {
    return axios.get<FormResponseDto>(
      `${API_URL}/api/forms/response/${responseId}`,
      getAuthHeader()
    );
  }
  static updateResponse(responseId: number, updatedResponse: FormResponseDto) {
    return axios.put<ResponseModel>(
      `${API_URL}/api/forms/response/${responseId}`,
      updatedResponse,
      getAuthHeader()
    );
  }

  static submitResponse(formId: number, payload: FormResponsePayload) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/forms/${formId}/response`,
      payload,
      {
        headers: {
          ...getAuthHeader().headers, // assuming getAuthHeader() returns `{ headers: { Authorization: 'Bearer ...' } }`
          "Content-Type": "application/json",
        },
      }
    );
  }
  static getFormByPageId(pageId: number) {
    return axios.get<FormDto>(
      `${API_URL}/api/forms/by-page/${pageId}`,
      getAuthHeader()
    );
  }
  static getFormByEventId(eventId: number) {
    return axios.get<FormDto>(
      `${API_URL}/api/forms/by-event/${eventId}`,
      getAuthHeader()
    );
  }
  static getAllForms() {
    return axios.get<FormDto[]>(`${API_URL}/api/forms`, getAuthHeader());
  }
  static getActiveForms() {
    return axios.get<FormDto[]>(`${API_URL}/api/forms/active`, getAuthHeader());
  }

  static createOrUpdateForm(form: FormDto) {
    const method = form.formId ? "put" : "post";
    const url = form.formId
      ? `${API_URL}/api/forms/${form.formId}`
      : `${API_URL}/api/forms`;

    return axios[method]<ResponseModel>(url, form, {
      headers: {
        ...getAuthHeader().headers, // assuming getAuthHeader() returns `{ headers: { Authorization: 'Bearer ...' } }`
        "Content-Type": "application/json",
      },
    });
  }

  static deleteForm(formId: number) {
    return axios.delete<boolean>(
      `${API_URL}/api/forms/${formId}`,
      getAuthHeader()
    );
  }
  static deleteResponse(responseId: number) {
    return axios.delete<boolean>(
      `${API_URL}/api/forms/responses/${responseId}`,
      getAuthHeader()
    );
  }
  static getResponses(formId: number) {
    return axios.get<FormResponseDto[]>(
      `${API_URL}/api/forms/${formId}/responses`,
      getAuthHeader()
    );
  }
  static getSubmittedFormIdsByUser(userId: number) {
    return axios.get<number[]>(
      `${API_URL}/api/forms/submitted/${userId}`,
      getAuthHeader()
    );
  }

  static hasUserSubmittedForm(formId: number, userId: number) {
    return axios.get<boolean>(
      `${API_URL}/api/forms/${formId}/has-submitted/${userId}`,
      getAuthHeader()
    );
  }

  static getAllFormResponses() {
    return axios.get<FormResponseDto[]>(
      `${API_URL}/api/forms/responses`,
      getAuthHeader()
    );
  }

  static downloadResponseReport(responseId: number) {
    return axios.get(`${API_URL}/api/forms/responses/${responseId}/report`, {
      responseType: "arraybuffer",
      headers: {
        ...getAuthHeader().headers,
        Accept: "application/pdf", // or application/octet-stream
      },
    });
  }

  static updateResponseStatus(responseId: number, status: string) {
    return axios.put(
      `${API_URL}/api/forms/responses/${responseId}/status`,
      { status },
      getAuthHeader()
    );
  }
}

export default FormApi;
